import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardIcon, UsersIcon, GameIcon, ListIcon, SearchIcon, SettingsIcon } from './Icons';
import './Layout.css';

const NavItem = ({ to, icon, label }) => (
  <NavLink to={to} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
    <span className="nav-icon">{icon}</span>
    <span className="nav-text">{label}</span>
  </NavLink>
);

const NavSection = ({ title, children }) => (
  <div className="nav-section">
    <div className="nav-section-title">{title}</div>
    {children}
  </div>
);

function Sidebar({ isOpen }) {
  const { admin, hasPermission } = useAuth();
  const canAccessProviderTickets = hasPermission('tickets', 'create');
  const canViewDashboard = hasPermission('dashboard', 'view');
  const canSearchMerchants = hasPermission('merchant_search', 'view');
  const canViewPaymentGateways = ['categories', 'sources', 'providers'].some(resource => hasPermission(`payment_gateway_${resource}`, 'view'));
  const canViewGameManagement = (
    hasPermission('countries', 'view') ||
    hasPermission('currencies', 'view') ||
    hasPermission('categories', 'view') ||
    hasPermission('sub_categories', 'view') ||
    hasPermission('sources', 'view') ||
    hasPermission('providers', 'view')
  );

  return (
    <aside id="app-sidebar" className={`sidebar ${!isOpen ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-brand-card">
          <img src="/logo_text_horizontal.png" alt="GamesFinance" className="sidebar-logo" />
          <div className="sidebar-brand-copy">Backoffice</div>
        </div>
      </div>

      <nav className="sidebar-nav">
        {canViewDashboard && (
          <NavSection title="Workspace">
            <NavItem to="/dashboard" icon={<DashboardIcon />} label="Dashboard" />
          </NavSection>
        )}

        {(hasPermission('merchants', 'view') || canSearchMerchants || canAccessProviderTickets || hasPermission('finance_reconciliation', 'view') || hasPermission('users', 'view')) && (
          <NavSection title="Operations">
            {hasPermission('merchants', 'view') && (
              <NavItem to="/merchants" icon={<UsersIcon />} label="Merchants" />
            )}
            {canSearchMerchants && (
              <NavItem to="/merchant-search" icon={<SearchIcon />} label="Merchant Search" />
            )}
            {canAccessProviderTickets && (
              <NavItem to="/tickets" icon={<ListIcon />} label="Ticketing" />
            )}
            {hasPermission('finance_reconciliation', 'view') && (
              <NavItem to="/finance-reconciliation" icon={<ListIcon />} label="Finance Reconciliation" />
            )}
            {hasPermission('users', 'view') && (
              <NavItem to="/users" icon={<UsersIcon />} label="Users" />
            )}
          </NavSection>
        )}

        {(hasPermission('master_providers', 'view') || hasPermission('payment_gateway_master_providers', 'view')) && (
          <NavSection title="L1 Master">
            {hasPermission('master_providers', 'view') && <NavItem to="/games/master-providers" icon={<GameIcon />} label="Providers" />}
            {hasPermission('payment_gateway_master_providers', 'view') && <NavItem to="/payment-gateways/master-providers" icon={<ListIcon />} label="Payment Gateway Providers" />}
          </NavSection>
        )}

        {canViewPaymentGateways && (
          <NavSection title="Payment Gateway Management">
            {hasPermission('payment_gateway_categories', 'view') && <NavItem to="/payment-gateways/categories" icon={<ListIcon />} label="Categories" />}
            {hasPermission('payment_gateway_sources', 'view') && <NavItem to="/payment-gateways/sources" icon={<ListIcon />} label="Provider Sources" />}
            {hasPermission('payment_gateway_providers', 'view') && <NavItem to="/payment-gateways/providers" icon={<ListIcon />} label="Providers" />}
          </NavSection>
        )}

        {canViewGameManagement && (
          <NavSection title="Game Management">
            {hasPermission('countries', 'view') && (
              <NavItem to="/games/countries" icon={<GameIcon />} label="Countries" />
            )}
            {hasPermission('currencies', 'view') && (
              <NavItem to="/games/currencies" icon={<GameIcon />} label="Currencies" />
            )}
            {hasPermission('categories', 'view') && (
              <NavItem to="/games/categories" icon={<GameIcon />} label="Categories" />
            )}
            {hasPermission('sub_categories', 'view') && (
              <NavItem to="/games/sub-categories" icon={<GameIcon />} label="Sub Categories" />
            )}
            {hasPermission('sources', 'view') && (
              <NavItem to="/games/sources" icon={<GameIcon />} label="Provider Sources" />
            )}
            {hasPermission('providers', 'view') && (
              <NavItem to="/games/providers" icon={<GameIcon />} label="Providers" />
            )}
          </NavSection>
        )}

        {hasPermission('audit_logs', 'view') && (
          <NavSection title="System">
            <NavItem to="/logs" icon={<ListIcon />} label="Audit Logs" />
            <NavItem to="/change-history" icon={<ListIcon />} label="Change History" />
            {admin?.role === 'super_admin' && <NavItem to="/settings" icon={<SettingsIcon />} label="Settings" />}
          </NavSection>
        )}
      </nav>

      <div className="sidebar-footer">
        <span>v1.0.0</span>
      </div>
    </aside>
  );
}

export default Sidebar;
